import { FC, MouseEvent, ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import styles from "@/components/anchorData/anchorData.module.css";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import DropDown from "@/components/forms/inputs/dropDown";
import classNames from "classnames";
import ObjectListInput from "@/components/forms/inputs/objectListInput";
import { Tooltip, FormControlLabel } from "@mui/material";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import { useTranslation } from "react-i18next";
import { MuiTelInput } from "mui-tel-input";
import ReplayIcon from "@mui/icons-material/Replay";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import CheckIcon from "@mui/icons-material/Check";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { replaceDate } from "@/helper/dateHandling";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { KAER_COMPANY_ID } from "@/helper/systemIds";
import DatePickerGeneric from "@/components/dateTime/DatePickerGeneric";
import dayjs from "dayjs";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { MandatoryMarker } from "@/components/forms/inputs/mandatoryMarker";

interface AnchorDataFormProps {
  fields: AnchorField[];
  mode: AnchorDataFormMode;
}
export type FieldKind =
  | "int"
  | "float"
  | "price"
  | "percent"
  | "str"
  | "date"
  | "enum"
  | "object_list"
  | "subject_id_list"
  | "multiline"
  | "email"
  | "phone_number"
  | "wz_code_id_list";

export type FieldKindExtended = FieldKind | "address";
export type TypeMapping = Record<string, FieldKind>;

type AnchorDataFormMode = "form" | "modal";

export interface AnchorField {
  name: string;
  label: string;
  required?: boolean;
  requiredForActivation?: boolean;
  highlightMissing?: boolean;
  type: FieldKind | FieldKindExtended;
  value?: any | ReactNode;
  options?: string[]; // only for enum
  is_calculated?: boolean;
  editable?: boolean;
  hiddenBySearch?: any;
  valid_from?: any;
  subdomain?: string;
  typeMapping?: TypeMapping;
  isValueInherited?: boolean;
  inheritedFrom?: {
    id: string;
    name: string;
  } | null;
  hasInheritedValues?: boolean;
  inheritedValue?: any;
  modifiedBy?: {
    id: string;
    name: string;
  } | null;
  modifiedOn?: string | null;
}
export interface SelectOption {
  value: string;
  label: string;
}

type UnknownSelectValue =
  | SelectOption
  | { id: string; name: string }
  | null
  | undefined;

// const hasRequiredValue = (value: unknown): boolean => {
//   if (value === null || value === undefined) return false;
//   if (typeof value === "string") return value.trim().length > 0;
//   if (Array.isArray(value)) return value.length > 0;
//   if (typeof value === "object") return Object.keys(value).length > 0;
//   return true;‚
// };

const AnchorDataForm: FC<AnchorDataFormProps> = ({ fields, mode }) => {
  const { control, formState } = useFormContext();

  const { t } = useTranslation();

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
  });

  const [copiedFieldName, setCopiedFieldName] = useState<string | null>(null);

  const copyFieldName = (name: string, e: MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(name);
    setCopiedFieldName(name);
    setTimeout(() => setCopiedFieldName(null), 2000);
  };

  const normalizeSelectValue = (
    input: UnknownSelectValue | UnknownSelectValue[]
  ): SelectOption[] => {
    if (!input) return [];

    const values = Array.isArray(input) ? input : [input];

    return values
      .filter((item): item is SelectOption | { id: string; name: string } =>
        Boolean(
          item &&
            typeof item === "object" &&
            (("value" in item && item.value && item.label) ||
              ("id" in item && item.id && item.name))
        )
      )
      .map((item) => {
        if ("id" in item) {
          return { value: item.id, label: item.name };
        }

        return item;
      });
  };

  const getLastEditedTooltip = (field: AnchorField) => {
    if (!field.modifiedBy?.name && !field.modifiedOn) {
      return null;
    }

    return (
      <Tooltip
        placement="top"
        arrow
        title={
          <div className={styles.tooltipContent}>
            <span>{t("last_edited")}</span>
            <br />
            <span className={styles.labelinheritedInfo}>
              {t("last_edited_by")}{" "}
            </span>
            {field.modifiedBy?.name || "-"}
            <br />
            <span className={styles.labelinheritedInfo}>
              {t("last_edited_on")}{" "}
            </span>
            {field.modifiedOn ? replaceDate(field.modifiedOn, true) : "-"}
          </div>
        }
      >
        <span className={styles.lastEditedIcon}>
          <ManageAccountsIcon fontSize="inherit" />
        </span>
      </Tooltip>
    );
  };

  const getInputLabel = (field: any, onChange?: any) => {
    const shouldShowReset = !field.isValueInherited && field.hasInheritedValues;
    return (
      <span
        className={styles.labelWithIcons}
        title={
          field.highlightMissing
            ? t("anchordata_missing_required_field")
            : undefined
        }
      >
        {field.isValueInherited && (
          <Tooltip
            placement="top"
            arrow
            title={
              <div className={styles.tooltipContent}>
                <span>{field.label}</span>
                <br />
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_value")}
                </span>
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_from")}:{" "}
                </span>
                {field.inheritedFrom?.name || "-"}
              </div>
            }
          >
            <span className={styles.inheritedIcon}>ⓘ</span>
          </Tooltip>
        )}
        {field.label}
        {(field.required || field.requiredForActivation) && <MandatoryMarker />}
        <Tooltip
          arrow
          placement="top"
          title={
            copiedFieldName === field.name
              ? t("variable_name_copied")
              : field.name
          }
        >
          <span
            className={classNames(styles.fieldNameCopyIcon, {
              [styles.fieldNameCopyIconCopied]: copiedFieldName === field.name,
            })}
            onClick={(e) => copyFieldName(field.name, e)}
          >
            {copiedFieldName === field.name ? (
              <CheckIcon fontSize="inherit" />
            ) : (
              <AnnouncementIcon fontSize="inherit" />
            )}
          </span>
        </Tooltip>
        {getLastEditedTooltip(field)}
        {shouldShowReset && (
          <Tooltip
            title={
              <div className={styles.tooltipContent}>
                <span>{field.label}</span>
                <br />
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_values_reset")}
                </span>
                <br />

                <span className={styles.labelinheritedInfo}>
                  {t("inherited_value")}:{" "}
                </span>
                {field.inheritedValue || "-"}
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_from")}:{" "}
                </span>
                {field.inheritedFrom?.name || "-"}
              </div>
            }
          >
            <span
              className={styles.resetIcon}
              onClick={(e) => {
                e.stopPropagation();
                if (onChange) {
                  onChange(null);
                }
              }}
            >
              <ReplayIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
      </span>
    );
  };

  return (
    <div
      className={classNames({
        [styles.formWrapperFormMode]: mode === "form",
        [styles.formWrapperModalMode]: mode === "modal",
      })}
    >
      {fields.map((field) => {
        const isDirty = !!formState.dirtyFields?.[field.name];

        const showInheritedStyle = field.isValueInherited && !isDirty;

        //currently all disabled and validationCheckes disabled for better testing

        // const inputIsDisabled = field.is_calculated;
        const inputIsDisabled = field.editable === false;
        // const validationRules = {
        //   validate: {
        //     value: (value: any) => {
        //       if (
        //         field.type === "email" &&
        //         !field.is_calculated &&
        //         !emailValidationRegex.test(value)
        //       ) {
        //         return t("enter_valid_email");
        //       }
        //     },
        //   },
        // };

        return (
          <div
            key={field.name}
            className={classNames({
              [styles.formFieldHidden]: field.hiddenBySearch,
              [styles.formFlexItemWide]:
                field.type === "object_list" || field.type === "multiline",
              [styles.formItemFormMode]: mode === "form",
              [styles.formItemModalMode]: mode === "modal",
              [styles.inheritedValuesWrapper]: showInheritedStyle,
              [styles.missingRequiredField]: field.highlightMissing,
            })}
          >
            <GenericControllerWithInput
              control={control}
              // rules={{
              //   validate: (value: unknown) =>
              //     !field.required ||
              //     hasRequiredValue(value) ||
              //     t("field_required"),
              // }}
              name={field.name}
              defaultValue={field.value ?? ""}
              renderInput={({ onChange, ref, value }) => {
                switch (field.type) {
                  case "int":
                    return (
                      <InputWithRef
                        name={field.name}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            onChange(null);
                            return;
                          }
                          onChange(raw);
                        }}
                        label={getInputLabel(field, onChange)}
                        ref={ref}
                        value={value}
                        type="int"
                        disabled={inputIsDisabled}
                        labelClassname={classNames(styles.label)}
                        onWheel={(e: any) => {
                          e.currentTarget.blur();
                        }}
                        inputClassname={classNames({
                          [styles.inheritedInputTextColor]: showInheritedStyle,
                        })}
                      />
                    );
                  case "float":
                    return (
                      <InputWithRef
                        name={field.name}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            onChange(null);
                            return;
                          }
                          onChange(raw);
                        }}
                        label={getInputLabel(field, onChange)}
                        ref={ref}
                        value={value}
                        type="float"
                        disabled={inputIsDisabled}
                        labelClassname={classNames(styles.label)}
                        onWheel={(e: any) => {
                          e.currentTarget.blur();
                        }}
                        inputClassname={classNames({
                          [styles.inheritedInputTextColor]: showInheritedStyle,
                        })}
                      />
                    );
                  case "price":
                    return (
                      <InputWithRef
                        name={field.name}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            onChange(null);
                            return;
                          }
                          if (/^[0-9]*([.,][0-9]*)?$/.test(raw)) {
                            onChange(raw);
                          }
                        }}
                        label={getInputLabel(field, onChange)}
                        ref={ref}
                        value={
                          value == null || value === "" ? "" : String(value)
                        }
                        type="text"
                        inputMode="decimal"
                        disabled={inputIsDisabled}
                        labelClassname={classNames(styles.label)}
                        onWheel={(e: any) => {
                          e.currentTarget.blur();
                        }}
                        inputClassname={classNames({
                          [styles.inheritedInputTextColor]: showInheritedStyle,
                        })}
                      />
                    );
                  case "percent": {
                    const [displayValue, setDisplayValue] = useState<string>(
                      () => {
                        if (value === null || value === undefined) return "";

                        const num =
                          typeof value === "number" ? value : Number(value);

                        if (!Number.isFinite(num)) return "";

                        return num.toFixed(2);
                      }
                    );
                    return (
                      <InputWithRef
                        name={field.name}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          if (/^[0-9]*([.,][0-9]*)?$/.test(raw) || raw === "") {
                            setDisplayValue(raw);
                            const num =
                              raw === "" ? null : Number(raw.replace(",", "."));
                            if (num !== null && !Number.isNaN(num)) {
                              onChange(num);
                            } else if (raw === "") {
                              onChange(null);
                            }
                          }
                        }}
                        onBlur={() => {
                          const num = Number(displayValue.replace(",", "."));
                          if (!isNaN(num)) {
                            setDisplayValue(num.toFixed(2).replace(".", ","));
                          }
                        }}
                        label={getInputLabel(field, onChange)}
                        ref={ref}
                        value={displayValue}
                        type="text"
                        disabled={inputIsDisabled}
                        onWheel={(e: any) => {
                          e.currentTarget.blur();
                        }}
                        labelClassname={classNames(styles.label)}
                        inputClassname={classNames({
                          [styles.inheritedInputTextColor]: showInheritedStyle,
                        })}
                      />
                    );
                  }
                  case "date":
                    return (
                      <>
                        <DatePickerGeneric
                          value={value ? dayjs(value) : null}
                          onChange={(date) => {
                            onChange(date ? date.format("YYYY-MM-DD") : null);
                          }}
                          disabled={inputIsDisabled}
                          label={getInputLabel(field, onChange)}
                          labelClassname={classNames(styles.label, {
                            [styles.label_max_width]: !field.isValueInherited,
                          })}
                          inputClassname={classNames({
                            [styles.inheritedInputTextColor]:
                              showInheritedStyle,
                          })}
                        />
                      </>
                    );
                  case "enum":
                    return (
                      <DropDown
                        classNamePrefix="anchor-data"
                        options={field.options}
                        onChange={(selectedOption: any) =>
                          onChange(selectedOption ? selectedOption.value : null)
                        }
                        name={field.name}
                        label={getInputLabel(field, onChange)}
                        defaultValue={value}
                        disabled={inputIsDisabled}
                        isClearable
                        labelClassName={classNames(styles.label)}
                        customStyles={
                          showInheritedStyle
                            ? {
                                singleValue: (baseStyles) => ({
                                  ...baseStyles,
                                  color: "#ba78de",
                                }),
                              }
                            : undefined
                        }
                      />
                    );
                  case "object_list":
                    return (
                      <ObjectListInput
                        name={field.name}
                        value={value}
                        onChange={onChange}
                        label={getInputLabel(field, onChange)}
                        labelOptions={field.options || []}
                        typeMapping={field.typeMapping}
                        isValueInherited={field.isValueInherited}
                        editable={field.editable}
                        tableCellClassname={classNames({
                          [styles.inheritedInputTextColor]: showInheritedStyle,
                        })}
                      />
                    );
                  case "subject_id_list": {
                    return (
                      <div>
                        <FormFieldLabel
                          label={getInputLabel(field, onChange)}
                          htmlFor={field.name}
                          className={classNames(styles.label)}
                        />
                        <SubjectSearchSelect
                          classNamePrefix="anchor-data"
                          name={field.name}
                          onChange={(selectedOption: any) =>
                            onChange(selectedOption)
                          }
                          isMulti={true}
                          isClearable={true}
                          value={normalizeSelectValue(value)}
                          loadOptions={(input: any) =>
                            fetchSubjects(input, {
                              entity: "subject/quicksearch",
                              queryParameter: "users",
                              companyIdForFiltering: KAER_COMPANY_ID,
                            })
                          }
                          customStyles={
                            showInheritedStyle
                              ? {
                                  singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    color: "#ba78de",
                                  }),
                                }
                              : undefined
                          }
                        />
                      </div>
                    );
                  }
                  case "multiline":
                    return (
                      <TextAreaWithRef
                        label={getInputLabel(field, onChange)}
                        name={field.name}
                        onChange={onChange}
                        ref={ref}
                        value={value}
                        labelClassname={classNames(styles.label)}
                      />
                    );
                  case "phone_number":
                    return (
                      <FormControlLabel
                        label={getInputLabel(field, onChange)}
                        labelPlacement="top"
                        disableTypography
                        sx={{
                          margin: "0",
                          width: "100%",
                          alignItems: "start",
                          fontFamily: "inherit !important",
                        }}
                        control={
                          <MuiTelInput
                            placeholder={t("input_your_phone_number")}
                            defaultCountry="DE"
                            langOfCountryName="DE"
                            value={value || ""}
                            onChange={onChange}
                            className={styles.telInput}
                            sx={{
                              "& .MuiInputBase-input": {
                                color: showInheritedStyle
                                  ? "#ba78de"
                                  : undefined,
                                padding: "0.91rem",
                              },
                            }}
                          />
                        }
                      />
                    );
                  case "wz_code_id_list": {
                    const transformValue = (data: any[]) => {
                      if (!Array.isArray(data) || value.length === 0) {
                        return [];
                      }

                      return value.map((item: any) => {
                        const label = item.label
                          ? item.label
                          : `${item.name} - ${item.description}`;
                        return {
                          value: item.id,
                          label: label,
                        };
                      });
                    };

                    const transformedValue = transformValue(value);

                    return (
                      <div>
                        <FormFieldLabel
                          label={getInputLabel(field, onChange)}
                          htmlFor={field.name}
                        />
                        <SubjectSearchSelect
                          classNamePrefix="anchor-data"
                          name={field.name}
                          isMulti={true}
                          isClearable={true}
                          value={transformedValue}
                          onChange={(selectedOption: any) =>
                            onChange(selectedOption)
                          }
                          loadOptions={(input: any) =>
                            fetchSubjects(input, {
                              entity: "wzCode",
                              mapResponseToOptions: (data) =>
                                data.results.map((item: any) => ({
                                  value: item.id,
                                  label: `${item.code} - ${item.description}`,
                                })),
                            }) || []
                          }
                          customStyles={
                            showInheritedStyle
                              ? {
                                  singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    color: "#ba78de",
                                  }),
                                }
                              : undefined
                          }
                        />
                      </div>
                    );
                  }
                  default:
                    return (
                      <>
                        <InputWithRef
                          name={field.name}
                          onChange={onChange}
                          label={getInputLabel(field, onChange)}
                          ref={ref}
                          value={value}
                          disabled={inputIsDisabled}
                          labelClassname={classNames(styles.label)}
                          inputClassname={classNames({
                            [styles.inheritedInputTextColor]:
                              showInheritedStyle,
                          })}
                        />
                      </>
                    );
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AnchorDataForm;
